/**
 * Un réducteur qui rend, avec l'état d'après, les signaux de sa transition (mesures, fils d'Ariane) :
 * ils partent au moment même de l'événement, un par un.
 *
 * `useReducer` ne le permet pas sans perte : ses transitions se rejouent au rendu, et deux
 * événements du même tour n'en donneraient qu'un à lire dans un effet. Ici l'état vit dans une
 * référence, chaque événement le fait avancer tout de suite, et le rendu suit.
 */
import { useCallback, useReducer, useRef } from "react"

export type SignalReducer<State, Event, Signal> = (state: State, event: Event) => { state: State; signals: Signal[] }

/** Le passage d'une phase à l'autre : le fil d'Ariane d'une machine (`telemetry.transition`). */
export type PhaseTransition = { from: string; to: string; event: string }

/**
 * Le réducteur pur d'une machine, qui rend en signal chaque changement de sa phase (`phaseOf`) : le
 * crochet le passe à `telemetry.transition`. Posé au niveau du module, il reste le même d'un rendu à
 * l'autre.
 */
export function withPhaseTransitions<State, Event extends { type: string }>(
    reducer: (state: State, event: Event) => State,
    phaseOf: (state: State) => string
): SignalReducer<State, Event, PhaseTransition> {
    return (state, event) => {
        const next = reducer(state, event)
        const from = phaseOf(state)
        const to = phaseOf(next)
        return { state: next, signals: from !== to ? [{ from, to, event: event.type }] : [] }
    }
}

export function useSignalReducer<State, Event, Signal>(
    reducer: SignalReducer<State, Event, Signal>,
    init: () => State,
    onSignals: (signals: Signal[]) => void
): [State, (event: Event) => void, () => State] {
    const stateRef = useRef<State | null>(null)
    if (stateRef.current === null) stateRef.current = init()
    const onSignalsRef = useRef(onSignals)
    onSignalsRef.current = onSignals
    const [, rerender] = useReducer((count: number) => count + 1, 0)

    const dispatch = useCallback(
        (event: Event) => {
            const { state, signals } = reducer(stateRef.current as State, event)
            if (state !== stateRef.current) {
                stateRef.current = state
                rerender()
            }
            if (signals.length > 0) onSignalsRef.current(signals)
        },
        [reducer]
    )

    const read = useCallback(() => stateRef.current as State, [])

    return [stateRef.current, dispatch, read]
}
